import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

// Charts for the team tabs (Nic 2026-10-25): a month charts by its days, a
// quarter by its three months (the server's `series`). The bucket running
// now is "so far" (lighter bar, shaded band); days still ahead and days
// before launch have no value, so the chart leaves them empty rather than
// drawing a misleading zero.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FULL_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// 'YYYY-MM-DD' -> "3" (axis) / "Nov 3" (tooltip); 'YYYY-MM' -> "Nov" /
// "November 2026". Parsed by parts, never new Date('YYYY-MM-DD').
export const bucketShort = (key = '') => {
  const [y, m, d] = key.split('-').map(Number);
  if (!m) return String(y || '');
  return d ? String(d) : MONTHS[m - 1] || '';
};
export const bucketLong = (key = '') => {
  const [y, m, d] = key.split('-').map(Number);
  if (!m) return String(y || '');
  return d ? `${MONTHS[m - 1]} ${d}` : `${FULL_MONTHS[m - 1]} ${y}`;
};

// The Funnel tab's buckets are always a first day ('YYYY-MM-DD') at a
// granularity of day, week, month or year; label them for that.
export const labelsFor = (granularity) => {
  const parts = (k = '') => k.split('-').map(Number);
  if (granularity === 'year') {
    return { short: (k) => String(parts(k)[0]), long: (k) => String(parts(k)[0]) };
  }
  if (granularity === 'month') {
    return {
      short: (k) => MONTHS[parts(k)[1] - 1] || '',
      long: (k) => `${FULL_MONTHS[parts(k)[1] - 1]} ${parts(k)[0]}`,
    };
  }
  const md = (k) => `${MONTHS[parts(k)[1] - 1]} ${parts(k)[2]}`;
  return granularity === 'week' ? { short: md, long: (k) => `Week of ${md(k)}` } : { short: md, long: md };
};

// Enough ticks to read, never crammed.
const tickEvery = (n, most) => (n > most ? Math.ceil(n / most) - 1 : 0);
export const isDaily = (series) => /^\d{4}-\d{2}-\d{2}$/.test(series?.[0]?.key || '');

// One row per bucket with the picked values; no-value buckets get nulls.
export const chartRows = (series, fields) =>
  (series || []).map((b) => {
    const empty = b.future || b.beforeTracking;
    const row = { key: b.key, partial: b.partial, empty };
    fields.forEach((f) => {
      row[f.key] = empty ? null : f.get(b);
    });
    return row;
  });

export const fmtPctV = (v) => (v == null ? '-' : `${Math.round(v)}%`);
// Minutes -> "45s", "1m 40s", "2h 05m".
export const fmtWait = (m) => {
  if (m == null) return '-';
  const s = Math.round(m * 60);
  if (s < 60) return `${s}s`;
  const mm = Math.floor(s / 60);
  if (mm < 60) return `${mm}m ${String(s % 60).padStart(2, '0')}s`;
  return `${Math.floor(mm / 60)}h ${String(mm % 60).padStart(2, '0')}m`;
};
export const fmtDaysV = (v) => (v == null ? '-' : v < 1 ? '< 1 day' : `${Math.round(v * 10) / 10} days`);
const fmtCount = (v) => (v == null ? '-' : Number(v).toLocaleString('en-US'));

// Dark tooltip shared by every chart. `fields` carry each value's format.
const Tip = ({ active, payload, label, fields, long = bucketLong }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload || {};
  if (row.empty) return null;
  const fmt = Object.fromEntries((fields || []).map((f) => [f.key, f.format || fmtCount]));
  // `__`-prefixed keys are drawing helpers (the zero base, the hot transfer
  // chart's goal ticks and labels), never values.
  const shown = payload.filter((p) => p.value != null && !String(p.dataKey).startsWith('__'));
  return (
    <div className="pointer-events-none rounded-lg bg-slate-900 px-3 py-2 text-[11px] text-white shadow-lg">
      <div className="mb-1 font-semibold">
        {long(label)}
        {row.partial ? <span className="ml-1 font-normal text-slate-400">so far</span> : null}
      </div>
      {shown.length ? (
        shown.map((p) => (
          <div key={p.dataKey} className="flex items-center gap-2 py-px">
            <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: p.color || p.fill }} />
            <span className="text-slate-300">{p.name}</span>
            <span className="ml-auto pl-4 font-semibold tabular-nums">{(fmt[p.dataKey] || fmtCount)(p.value)}</span>
          </div>
        ))
      ) : (
        <div className="text-slate-400">Nothing yet</div>
      )}
    </div>
  );
};

const AXIS_TICK = { fill: '#94A3B8', fontSize: 11 };

// Charts redraw once a window resize settles, not on every pixel of it (Nic
// 2026-10-25: a fast resize left a white band trailing the edge while a
// dozen charts re-measured).
const RESIZE_SETTLE_MS = 120;
// A just-flipped tile mounts its chart before the box is measured; recharts
// then sees -1 x -1 and warns in the console. Start at 1 x 1 instead; it
// takes the real size right after.
const FIRST_SIZE = { width: 1, height: 1 };

// The tile backs: small bars over the period, stacked when several fields.
// `fields` = [{ key, name, color, get, format }].
export const MiniBars = ({ series, fields, short = bucketShort, long = bucketLong }) => {
  const data = chartRows(series, fields);
  const many = data.length > 12;
  const hasAny = data.some((r) => fields.some((f) => r[f.key]));
  return (
    <div className="relative h-full w-full">
      {!hasAny ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center text-[11px] text-slate-400">
          Nothing in this period yet
        </div>
      ) : null}
      <ResponsiveContainer width="100%" height="100%" debounce={RESIZE_SETTLE_MS} initialDimension={FIRST_SIZE}>
        <BarChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }} barCategoryGap={many ? '18%' : '30%'}>
          <XAxis
            dataKey="key"
            tickFormatter={short}
            interval={tickEvery(data.length, 5)}
            tickLine={false}
            axisLine={{ stroke: '#E2E8F0' }}
            tick={{ fill: '#94A3B8', fontSize: 9 }}
            height={14}
          />
          <YAxis hide allowDecimals={false} />
          <Tooltip
            cursor={{ fill: 'rgba(148,163,184,0.14)' }}
            content={<Tip fields={fields} long={long} />}
            position={{ y: -6 }}
            isAnimationActive={false}
          />
          {fields.map((f, i) => (
            <Bar
              key={f.key}
              dataKey={f.key}
              name={f.name}
              stackId="s"
              fill={f.color}
              radius={i === fields.length - 1 ? [3, 3, 0, 0] : 0}
              isAnimationActive={false}
            >
              {data.map((r) => (
                <Cell key={r.key} fillOpacity={r.partial ? 0.5 : 1} />
              ))}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// The goal line's label: an amber pill at the right end ("GOAL 90%").
const GoalLabel = ({ viewBox, text }) => {
  if (!viewBox) return null;
  const w = text.length * 6.2 + 14;
  return (
    <g transform={`translate(${viewBox.x + viewBox.width - w},${viewBox.y - 9})`}>
      <rect width={w} height={18} rx={4} fill="#FEF3C7" />
      <text x={w / 2} y={12.5} textAnchor="middle" fontSize={10} fontWeight={700} fill="#B45309" letterSpacing="0.04em">
        {text}
      </text>
    </g>
  );
};

// The big chart: bars and lines over the period on up to two axes, an
// optional dashed goal, the "so far" band, and pill legends underneath.
// fields: [{ key, name, color, get, format, type: 'bar' | 'line',
//            axis: 'left' | 'right', stack }]
export const TrendChart = ({
  series,
  fields,
  height = 260,
  leftFormat = (v) => v,
  rightFormat = (v) => v,
  leftDomain,
  rightDomain,
  goal,
  leftLabel,
  rightLabel,
  short = bucketShort,
  long = bucketLong,
}) => {
  // `__base`: an invisible zero on every bucket, so the axes and the "so
  // far" band draw even before there are any numbers (Nic 2026-10-25: a
  // chart with nothing yet dropped its band; one with zeros kept it).
  const data = chartRows(series, fields).map((r) => ({ ...r, __base: 0 }));
  const daily = data.length > 12;
  const partial = data.find((r) => r.partial);
  const hasRight = fields.some((f) => f.axis === 'right');
  const bars = fields.filter((f) => f.type !== 'line');
  const lastOfStack = (f) => {
    const same = bars.filter((b) => (b.stack || b.key) === (f.stack || f.key));
    return same[same.length - 1].key === f.key;
  };
  return (
    <div>
      <ResponsiveContainer width="100%" height={height} debounce={RESIZE_SETTLE_MS} initialDimension={FIRST_SIZE}>
        <ComposedChart data={data} margin={{ top: 16, right: hasRight ? 4 : 12, left: 0, bottom: 0 }} barCategoryGap={daily ? '22%' : '38%'}>
          <CartesianGrid strokeDasharray="3 4" stroke="#E2E8F0" vertical={false} />
          {partial ? (
            <ReferenceArea
              yAxisId="left"
              x1={partial.key}
              x2={partial.key}
              fill="#94A3B8"
              fillOpacity={0.12}
              ifOverflow="extendDomain"
              label={
                daily
                  ? undefined
                  : { value: 'SO FAR', position: 'insideTop', fill: '#94A3B8', fontSize: 10, fontWeight: 700 }
              }
            />
          ) : null}
          <XAxis
            dataKey="key"
            tickFormatter={short}
            interval={tickEvery(data.length, 12)}
            tickLine={false}
            axisLine={{ stroke: '#E2E8F0' }}
            tick={AXIS_TICK}
          />
          <YAxis
            yAxisId="left"
            tickFormatter={leftFormat}
            domain={leftDomain || [0, 'auto']}
            allowDecimals={false}
            tickLine={false}
            axisLine={false}
            tick={AXIS_TICK}
            width={44}
            label={
              leftLabel
                ? { value: leftLabel, angle: -90, position: 'insideLeft', fill: '#94A3B8', fontSize: 10, fontWeight: 600, dy: 40 }
                : undefined
            }
          />
          {hasRight ? (
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={rightFormat}
              domain={rightDomain || [0, 'auto']}
              tickLine={false}
              axisLine={false}
              tick={AXIS_TICK}
              width={48}
              label={
                rightLabel
                  ? { value: rightLabel, angle: 90, position: 'insideRight', fill: '#94A3B8', fontSize: 10, fontWeight: 600, dy: -30 }
                  : undefined
              }
            />
          ) : null}
          {goal ? (
            <ReferenceLine
              yAxisId={goal.axis || 'left'}
              y={goal.value}
              stroke="#F59E0B"
              strokeDasharray="6 4"
              strokeWidth={1.5}
              label={<GoalLabel text={goal.label} />}
            />
          ) : null}
          <Tooltip
            cursor={{ fill: 'rgba(148,163,184,0.12)' }}
            content={<Tip fields={fields} long={long} />}
            isAnimationActive={false}
          />
          <Line
            yAxisId="left"
            dataKey="__base"
            stroke="transparent"
            dot={false}
            activeDot={false}
            legendType="none"
            tooltipType="none"
            isAnimationActive={false}
          />
          {hasRight ? (
            <Line
              yAxisId="right"
              dataKey="__base"
              stroke="transparent"
              dot={false}
              activeDot={false}
              legendType="none"
              tooltipType="none"
              isAnimationActive={false}
            />
          ) : null}
          {fields.map((f) =>
            f.type === 'line' ? (
              <Line
                key={f.key}
                yAxisId={f.axis || 'left'}
                type="monotone"
                dataKey={f.key}
                name={f.name}
                stroke={f.color}
                strokeWidth={2.25}
                dot={{ r: daily ? 2 : 3.5, fill: f.color, strokeWidth: 0 }}
                activeDot={{ r: 5 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ) : (
              <Bar
                key={f.key}
                yAxisId={f.axis || 'left'}
                dataKey={f.key}
                name={f.name}
                stackId={f.stack}
                fill={f.color}
                radius={lastOfStack(f) ? [4, 4, 0, 0] : 0}
                maxBarSize={daily ? 22 : 64}
                isAnimationActive={false}
              >
                {data.map((r) => (
                  <Cell key={r.key} fillOpacity={r.partial ? 0.55 : 1} />
                ))}
              </Bar>
            ),
          )}
        </ComposedChart>
      </ResponsiveContainer>
      <Legend fields={fields} />
    </div>
  );
};

// Hot transfers by bucket (Nic 2026-11-04, option A of the mockups with his
// edits): each bar is that bucket's clients signed on a call, so Not sent
// (hatched) shows and the bars add up to the card's boxes. The sent share
// is the violet line on the right % axis, against the full-width dashed
// goal line, as before. Empty buckets say why, and "so far" goes under the
// label, never on the bar (both only with 12 buckets or fewer; a month by
// day would crowd them). The tooltip always has the numbers.
const HATCH_ID = 'hot-transfer-not-sent';
const HOT_FIELDS = [
  { key: 'live', name: 'Live', color: '#10B981' },
  { key: 'callback', name: 'Callback', color: '#F59E0B' },
  { key: 'settling', name: 'Settling', color: '#0EA5E9' },
  { key: 'notSent', name: 'Not sent', color: '#94A3B8', hatch: true },
  { key: 'sentPct', name: 'Sent share', color: '#8B5CF6', line: true, format: fmtPctV },
];
const HOT_BARS = HOT_FIELDS.filter((f) => !f.line);

export const HotTransfersChart = ({ series, goalPct = 80, height = 260, short = bucketShort, long = bucketLong }) => {
  const data = (series || []).map((b) => {
    const empty = b.future || b.beforeTracking;
    const signedOnCall = b.hotSignedOnCall || 0;
    const sent = b.hotSent || 0;
    return {
      key: b.key,
      partial: b.partial,
      future: !!b.future,
      empty,
      live: empty ? null : b.hotLive || 0,
      callback: empty ? null : b.hotCallback || 0,
      settling: empty ? null : b.hotSettling || 0,
      notSent: empty ? null : Math.max(0, signedOnCall - sent),
      sentPct: !empty && signedOnCall ? (b.sentPct ?? Math.round((sent / signedOnCall) * 100)) : null,
      __base: 0,
      // Drawing helper: where a bucket with no signings gets its note.
      __note: signedOnCall ? null : 0,
    };
  });
  const few = data.length <= 12;
  const partial = data.find((r) => r.partial);

  const EmptyNote = ({ cx, cy, payload }) =>
    !few || payload?.__note == null ? null : (
      <text x={cx} y={cy - 10} textAnchor="middle" fontSize={11} fill="#94A3B8">
        {payload.future ? 'still to come' : 'no signings'}
      </text>
    );
  const Tick = ({ x, y, payload }) => {
    const row = data.find((r) => r.key === payload.value);
    return (
      <g transform={`translate(${x},${y})`}>
        <text y={12} textAnchor="middle" fontSize={11} fill="#94A3B8">
          {short(payload.value)}
        </text>
        {few && row?.partial ? (
          <text y={26} textAnchor="middle" fontSize={10} fontWeight={600} fill="#94A3B8">
            so far
          </text>
        ) : null}
      </g>
    );
  };
  const helper = (axis, key, dot) => (
    <Line
      key={`${axis}${key}`}
      yAxisId={axis}
      dataKey={key}
      stroke="transparent"
      dot={dot}
      activeDot={false}
      legendType="none"
      tooltipType="none"
      connectNulls={false}
      isAnimationActive={false}
    />
  );

  return (
    <div>
      <ResponsiveContainer width="100%" height={height} debounce={RESIZE_SETTLE_MS} initialDimension={FIRST_SIZE}>
        <ComposedChart data={data} margin={{ top: 16, right: 4, left: 0, bottom: 0 }} barCategoryGap={few ? '38%' : '22%'}>
          <defs>
            <pattern id={HATCH_ID} width={6} height={6} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <line x1={0} y1={0} x2={0} y2={6} stroke="#94A3B8" strokeWidth={2.5} />
            </pattern>
          </defs>
          <CartesianGrid strokeDasharray="3 4" stroke="#E2E8F0" vertical={false} />
          {partial ? (
            <ReferenceArea yAxisId="left" x1={partial.key} x2={partial.key} fill="#94A3B8" fillOpacity={0.1} ifOverflow="extendDomain" />
          ) : null}
          <XAxis
            dataKey="key"
            tick={<Tick />}
            interval={tickEvery(data.length, 12)}
            tickLine={false}
            axisLine={{ stroke: '#E2E8F0' }}
            height={few ? 34 : 22}
          />
          <YAxis
            yAxisId="left"
            domain={[0, 'auto']}
            allowDecimals={false}
            tickLine={false}
            axisLine={false}
            tick={AXIS_TICK}
            width={44}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[0, 100]}
            tickFormatter={(v) => `${v}%`}
            tickLine={false}
            axisLine={false}
            tick={AXIS_TICK}
            width={48}
          />
          <ReferenceLine
            yAxisId="right"
            y={goalPct}
            stroke="#F59E0B"
            strokeDasharray="6 4"
            strokeWidth={1.5}
            label={<GoalLabel text={`GOAL ${goalPct}%`} />}
          />
          <Tooltip
            cursor={{ fill: 'rgba(148,163,184,0.12)' }}
            content={<Tip fields={HOT_FIELDS} long={long} />}
            isAnimationActive={false}
          />
          {helper('left', '__base', false)}
          {helper('right', '__base', false)}
          {HOT_BARS.map((f, i) => (
            <Bar
              key={f.key}
              yAxisId="left"
              dataKey={f.key}
              name={f.name}
              stackId="hot"
              fill={f.hatch ? `url(#${HATCH_ID})` : f.color}
              stroke={f.hatch ? f.color : 'none'}
              strokeWidth={f.hatch ? 1 : 0}
              radius={i === HOT_BARS.length - 1 ? [4, 4, 0, 0] : 0}
              maxBarSize={few ? 64 : 22}
              isAnimationActive={false}
            >
              {data.map((r) => (
                <Cell key={r.key} fillOpacity={r.partial ? 0.6 : 1} />
              ))}
            </Bar>
          ))}
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="sentPct"
            name="Sent share"
            stroke="#8B5CF6"
            strokeWidth={2.25}
            dot={{ r: few ? 4 : 2, fill: '#8B5CF6', strokeWidth: 0 }}
            activeDot={{ r: 5 }}
            connectNulls={false}
            isAnimationActive={false}
          />
          {helper('left', '__note', <EmptyNote />)}
        </ComposedChart>
      </ResponsiveContainer>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {HOT_FIELDS.map((f) => (
          <span
            key={f.key}
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium text-slate-600 ring-1 ring-inset ring-slate-200 dark:text-slate-300 dark:ring-white/10"
          >
            <span
              className={f.line ? 'h-0.5 w-3 rounded-full' : 'h-2.5 w-2.5 rounded-sm'}
              style={
                f.hatch
                  ? { background: `repeating-linear-gradient(135deg, ${f.color} 0 2px, transparent 2px 4px)`, boxShadow: `inset 0 0 0 1px ${f.color}` }
                  : { background: f.color }
              }
            />
            {f.name}
            {f.line ? <span className="text-[10px] uppercase tracking-wider text-slate-400">right</span> : null}
          </span>
        ))}
      </div>
    </div>
  );
};

// Pill legends (the V3 deck's recipe).
export const Legend = ({ fields }) => (
  <div className="mt-3 flex flex-wrap items-center gap-2">
    {fields.map((f) => (
      <span
        key={f.key}
        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium text-slate-600 ring-1 ring-inset ring-slate-200 dark:text-slate-300 dark:ring-white/10"
      >
        <span className={f.type === 'line' ? 'h-0.5 w-3 rounded-full' : 'h-2.5 w-2.5 rounded-sm'} style={{ background: f.color }} />
        {f.name}
        {f.axisNote ? <span className="text-[10px] uppercase tracking-wider text-slate-400">{f.axisNote}</span> : null}
      </span>
    ))}
  </div>
);
